import React from 'react';
import { t, Trans } from '@lingui/macro';

import { Text } from '@rover/kibble/core';

import { MarketingOptInVariationKey } from './types';

export type VariationContent = {
  image: string;
  imageDescription: string;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  mainCTALabel: string | React.ReactNode;
  secondaryCTALabel: string | React.ReactNode;
  tertiaryCTALabel: string | React.ReactNode;
};

export const MARKETING_OPT_IN = {
  title: () => <Trans>Get discounts, pet care tips, and more from Rover</Trans>,
  emailDescription: () => (
    <Trans>
      Sign up to get occasional pet care tips, exclusive discounts, offers, and updates from Rover
      and its affiliates through <Text fontWeight="semibold">email</Text>. You can opt out at any
      time.
    </Trans>
  ),
  emailSmsDescription: () => (
    <Trans>
      Sign up to get occasional pet care tips, exclusive discounts, offers, and updates from Rover
      and its affiliates through <Text fontWeight="semibold">email and text messages</Text>. You can
      opt out at any time.
    </Trans>
  ),
  emailWhatsappDescription: () => (
    <Trans>
      Sign up to get occasional pet care tips, exclusive discounts, offers, and updates from Rover
      and its affiliates through <Text fontWeight="semibold">email and WhatsApp</Text>. You can opt
      out at any time.
    </Trans>
  ),
  mainCta: () => <Trans>Yes, I want tips and discounts</Trans>,
  secondaryCta: () => <Trans>No thanks</Trans>,
  tertiaryCta: () => <Trans>Maybe later</Trans>,
  dogEmailImageDescription: (): string => t`Dog with email icon`,
  dogEmailSmsImageDescription: (): string => t`Dog with email and SMS icons`,
  dogEmailWhatsappImageDescription: (): string => t`Dog with email and WhatsApp icons`,
  catEmailImageDescription: (): string => t`Cat with email icon`,
  catEmailSmsImageDescription: (): string => t`Cat with email and SMS icons`,
  catEmailWhatsappImageDescription: (): string => t`Cat with email and WhatsApp icons`,
};

type ImageSet = {
  DogEmail: string;
  DogEmailSms: string;
  DogEmailWhatsapp: string;
  CatEmail: string;
  CatEmailSms: string;
  CatEmailWhatsapp: string;
};

export const buildVariationContent = (
  images: ImageSet,
  variationKey: MarketingOptInVariationKey
): VariationContent => {
  const baseContent = {
    title: MARKETING_OPT_IN.title(),
    description: MARKETING_OPT_IN.emailDescription(),
    mainCTALabel: MARKETING_OPT_IN.mainCta(),
    secondaryCTALabel: MARKETING_OPT_IN.secondaryCta(),
    tertiaryCTALabel: MARKETING_OPT_IN.tertiaryCta(),
  };

  const content: Record<MarketingOptInVariationKey, VariationContent> = {
    dog_email: {
      ...baseContent,
      image: images.DogEmail,
      imageDescription: MARKETING_OPT_IN.dogEmailImageDescription(),
    },
    dog_email_sms: {
      ...baseContent,
      image: images.DogEmailSms,
      imageDescription: MARKETING_OPT_IN.dogEmailSmsImageDescription(),
      description: MARKETING_OPT_IN.emailSmsDescription(),
    },
    dog_email_whatsapp: {
      ...baseContent,
      image: images.DogEmailWhatsapp,
      imageDescription: MARKETING_OPT_IN.dogEmailWhatsappImageDescription(),
      description: MARKETING_OPT_IN.emailWhatsappDescription(),
    },
    cat_email: {
      ...baseContent,
      image: images.CatEmail,
      imageDescription: MARKETING_OPT_IN.catEmailImageDescription(),
    },
    cat_email_sms: {
      ...baseContent,
      image: images.CatEmailSms,
      imageDescription: MARKETING_OPT_IN.catEmailSmsImageDescription(),
      description: MARKETING_OPT_IN.emailSmsDescription(),
    },
    cat_email_whatsapp: {
      ...baseContent,
      image: images.CatEmailWhatsapp,
      imageDescription: MARKETING_OPT_IN.catEmailWhatsappImageDescription(),
      description: MARKETING_OPT_IN.emailWhatsappDescription(),
    },
  };

  return content[variationKey];
};
